/**
 * Pipeline Orchestrator
 * Manages the 6 sequential GPT agents + image generation
 *
 * Pipeline Flow:
 * 1. Stratège (Strategy) - Decode domain, analyze SERP, define positioning
 * 2. Identité (Identity) - Create brand persona, tone, style
 * 3. Architecte (Architect) - Site structure, navigation, wireframes
 * 4. Rédacteur (Writer) - Content briefs, SEO, FAQs
 * 5. DA (Art Director) - Visual design, colors, typography
 * 6. Content Writer - Final HTML generation
 * 7. Images - Generate images from HTML references
 */

import { callLLM, setLLMListener, clearLLMListener } from '@services/llm'
import { analyzeSERP } from '@services/perplexity'
import { generateImage, extractImagesFromHTML } from '@services/imagen'
import { deductCredits, hasCredits } from '@services/stripe'
import { summarize } from './summarizer'
import { getStats, getStatsSummary, resetStats } from '@services/tokenStats'

// Pipeline phases
export const PHASES = {
  INIT: 'init',
  SERP: 'serp',           // Multi-provider web search (Perplexity/Claude/GPT/Gemini)
  STRATEGE: 'stratege',
  IDENTITE: 'identite',
  ARCHITECTE: 'architecte',
  REDACTEUR: 'redacteur',
  DA: 'da',
  CONTENT: 'content',
  IMAGES: 'images',
  DONE: 'done',
}

// Phase types for dual-model configuration
export const PHASE_TYPES = {
  REASONING: 'reasoning',   // Analysis, strategy, planning phases
  PRODUCTION: 'production', // Final content generation phases
}

// Map phases to their types
export const PHASE_TYPE_MAP = {
  [PHASES.SERP]: PHASE_TYPES.REASONING,
  [PHASES.STRATEGE]: PHASE_TYPES.REASONING,
  [PHASES.IDENTITE]: PHASE_TYPES.REASONING,
  [PHASES.ARCHITECTE]: PHASE_TYPES.REASONING,
  [PHASES.REDACTEUR]: PHASE_TYPES.REASONING,
  [PHASES.DA]: PHASE_TYPES.REASONING,
  [PHASES.CONTENT]: PHASE_TYPES.PRODUCTION,
  [PHASES.IMAGES]: PHASE_TYPES.PRODUCTION,
}

// Phase metadata
export const PHASE_INFO = {
  [PHASES.INIT]: {
    name: 'Initialisation',
    description: 'Analyse du domaine et configuration',
    icon: 'Zap',
    creditCost: 0,
  },
  [PHASES.SERP]: {
    name: 'Analyse SERP',
    description: 'Recherche web et analyse de la concurrence',
    icon: 'Globe',
    creditCost: 1,
  },
  [PHASES.STRATEGE]: {
    name: 'Stratège',
    description: 'Décodage NDD, positionnement, angle différenciant',
    icon: 'Target',
    creditCost: 1,
  },
  [PHASES.IDENTITE]: {
    name: 'Identité',
    description: 'Persona, ton éditorial, style rédactionnel',
    icon: 'Users',
    creditCost: 1,
  },
  [PHASES.ARCHITECTE]: {
    name: 'Architecte',
    description: 'Arborescence, maillage, wireframes',
    icon: 'Map',
    creditCost: 1,
  },
  [PHASES.REDACTEUR]: {
    name: 'Brief SEO',
    description: 'Planification contenu: structure, mots-clés, FAQs par page',
    icon: 'FileText',
    creditCost: 1,
  },
  [PHASES.DA]: {
    name: 'Directeur Artistique',
    description: 'Palette couleurs, typographies, style visuel',
    icon: 'Palette',
    creditCost: 1,
  },
  [PHASES.CONTENT]: {
    name: 'Content Writer',
    description: 'Génération HTML des pages',
    icon: 'Code',
    creditCost: 1, // Per batch
  },
  [PHASES.IMAGES]: {
    name: 'Images',
    description: 'Génération des visuels',
    icon: 'Image',
    creditCost: 1, // Per image
  },
  [PHASES.DONE]: {
    name: 'Terminé',
    description: 'Site prêt à déployer',
    icon: 'CheckCircle',
    creditCost: 0,
  },
}

/**
 * Check if deep debug mode is enabled (restricted to authorized users)
 */
function isDeepDebugEnabled() {
  try {
    const authorizedEmails = ['sebastien@koeki.fr', 'sebastien.grillt@koeki.fr']
    const userEmail = localStorage.getItem('emd_user_email') || ''
    const deepDebug = localStorage.getItem('EMD_DEEP_DEBUG') === 'true'
    return deepDebug && authorizedEmails.includes(userEmail.toLowerCase())
  } catch {
    return false
  }
}

// Expose toggle to window for authorized users
if (typeof window !== 'undefined') {
  window.EMD_DEEP_DEBUG = {
    enable: (email) => {
      localStorage.setItem('emd_user_email', email)
      localStorage.setItem('EMD_DEEP_DEBUG', 'true')
      console.log('🔐 Deep Debug mode enabled for:', email)
    },
    disable: () => {
      localStorage.setItem('EMD_DEEP_DEBUG', 'false')
      console.log('🔐 Deep Debug mode disabled')
    },
    status: () => isDeepDebugEnabled(),
  }
}

/**
 * Orchestrator class
 * Manages the entire pipeline execution
 */
export class Orchestrator {
  constructor(options = {}) {
    this.domain = null
    this.phase = PHASES.INIT
    this.context = {}
    this.generatedFiles = {}
    this.images = []

    // LLM configuration
    this.provider = options.provider || 'claude'
    this.model = options.model || 'sonnet'

    // Dual-model configuration (different LLMs for reasoning vs production)
    this.useDualModel = options.useDualModel || false
    this.reasoningModel = options.reasoningModel || 'sonnet'
    this.productionModel = options.productionModel || 'opus'

    // Cross-provider mode (anti-fingerprint): use different providers for reasoning vs production
    this.reasoningProvider = options.reasoningProvider || null
    this.productionProvider = options.productionProvider || null

    // Language configuration (from localStorage or options)
    this.language = options.language || localStorage.getItem('emd_language') || 'fr'

    // CMS Pattern for anti-footprint (tailwind, wordpress, webflow, drupal, joomla)
    this.cmsPattern = options.cmsPattern || localStorage.getItem('emd_cms_pattern') || 'tailwind'

    // Target site (Money Site) - the site this EMD should backlink to
    this.targetSite = options.targetSite || localStorage.getItem('emd_target_site') || null

    // Resource links for webography (up to 5 URLs to distribute as sources)
    this.resourceLinks = options.resourceLinks || (() => {
      try {
        return JSON.parse(localStorage.getItem('emd_resource_links') || '[]')
      } catch { return [] }
    })()

    // Search provider for SERP analysis (perplexity, claude, openai, gemini, multi, auto)
    this.searchProvider = options.searchProvider || localStorage.getItem('emd_search_provider') || 'auto'

    // Anti-footprint mode (fake CDN, tracking pixels, cookie banners)
    this.antiFootprint = options.antiFootprint !== undefined
      ? options.antiFootprint
      : localStorage.getItem('emd_anti_footprint') !== 'false' // default true

    // Site size (max pages to generate)
    this.maxPages = options.maxPages || parseInt(localStorage.getItem('emd_max_pages') || '15', 10)

    // Callbacks
    this.onPhaseChange = options.onPhaseChange || (() => {})
    this.onProgress = options.onProgress || (() => {})
    this.onMessage = options.onMessage || (() => {})
    this.onError = options.onError || (() => {})
    this.onFileGenerated = options.onFileGenerated || (() => {})
    this.onSummary = options.onSummary || (() => {})
    this.onDebugContext = options.onDebugContext || (() => {}) // NEW: Deep debug callback

    // Stats tracking
    this.startTime = null
    this.totalTokens = 0

    // Callback for final stats display
    this.onStats = options.onStats || (() => {})
  }

  /**
   * Get the appropriate provider and model for a phase based on dual-model configuration
   * @param {string} phase - The pipeline phase
   * @returns {{ provider: string, model: string }} The provider and model to use
   */
  getConfigForPhase(phase) {
    if (!this.useDualModel) {
      return { provider: this.provider, model: this.model }
    }

    const phaseType = PHASE_TYPE_MAP[phase]
    if (phaseType === PHASE_TYPES.PRODUCTION) {
      const provider = this.productionProvider || this.provider
      const model = this.productionModel
      console.log(`🎯 [ORCHESTRATOR] Phase ${phase} → Production: ${provider}/${model}`)
      return { provider, model }
    }

    const provider = this.reasoningProvider || this.provider
    const model = this.reasoningModel
    console.log(`🧠 [ORCHESTRATOR] Phase ${phase} → Reasoning: ${provider}/${model}`)
    return { provider, model }
  }

  /**
   * Get the appropriate model for a phase (legacy, for compatibility)
   * @param {string} phase - The pipeline phase
   * @returns {string} The model to use
   */
  getModelForPhase(phase) {
    return this.getConfigForPhase(phase).model
  }

  /**
   * Start pipeline with a domain
   */
  async start(domain) {
    this.domain = domain
    this.phase = PHASES.INIT
    this.context = {
      domain,
      language: this.language,
      cmsPattern: this.cmsPattern,
      targetSite: this.targetSite, // Money site to backlink
      resourceLinks: this.resourceLinks, // Additional links for webography
      antiFootprint: this.antiFootprint,
      maxPages: this.maxPages, // Site size limit
    }
    this.startTime = Date.now()

    // Reset token stats for this session
    resetStats()

    this.onPhaseChange(PHASES.INIT)
    this.onMessage({
      role: 'assistant',
      content: `Démarrage du pipeline pour **${domain}** (${this.language.toUpperCase()})...`,
    })

    try {
      // Run through all phases
      await this.runPhase(PHASES.STRATEGE)
      await this.runPhase(PHASES.IDENTITE)
      await this.runPhase(PHASES.ARCHITECTE)
      await this.runPhase(PHASES.REDACTEUR)
      await this.runPhase(PHASES.DA)
      await this.runPhase(PHASES.CONTENT)
      await this.runPhase(PHASES.IMAGES)

      this.phase = PHASES.DONE
      this.onPhaseChange(PHASES.DONE)

      // Get and display final token stats
      const stats = getStatsSummary()
      console.log('💰 [ORCHESTRATOR] ═══════════════════════════════════════')
      console.log('💰 [ORCHESTRATOR] STATS FINALES DU PIPELINE')
      console.log('💰 [ORCHESTRATOR] ═══════════════════════════════════════')
      console.log(`💰 [ORCHESTRATOR] Coût total: ${stats.totalCost}`)
      console.log(`💰 [ORCHESTRATOR] Tokens: ${stats.inputTokens.toLocaleString()} in → ${stats.outputTokens.toLocaleString()} out`)
      console.log(`💰 [ORCHESTRATOR] Appels LLM: ${stats.calls}`)
      console.log(`💰 [ORCHESTRATOR] Durée: ${stats.duration}s`)
      console.log('💰 [ORCHESTRATOR] Par phase:')
      stats.costPerPhase.forEach(p => {
        console.log(`💰   ${p.phase}: ${p.cost} (${p.tokens.toLocaleString()} tokens) [${p.model}]`)
      })
      console.log('💰 [ORCHESTRATOR] ═══════════════════════════════════════')

      // Emit stats to callback
      this.onStats(stats)

      return {
        success: true,
        files: this.generatedFiles,
        images: this.images,
        context: this.context,
        stats, // Include stats in return
      }
    } catch (error) {
      this.onError(error)
      // Still get stats even on error
      const stats = getStatsSummary()
      return {
        success: false,
        error: error.message,
        phase: this.phase,
        stats,
      }
    }
  }

  /**
   * Run a single phase
   */
  async runPhase(phase) {
    const info = PHASE_INFO[phase]
    const deepDebug = isDeepDebugEnabled()

    // Check credits
    if (!hasCredits('agentPhase', info.creditCost)) {
      throw new Error(`Crédits insuffisants pour la phase ${info.name}`)
    }

    this.phase = phase
    this.onPhaseChange(phase)
    this.onProgress({ phase, status: 'running', message: info.description })

    // DEEP DEBUG: Set up LLM listener to capture what's ACTUALLY sent/received
    if (deepDebug) {
      setLLMListener((llmData) => {
        // Skip summarizer calls (always uses haiku)
        if (llmData.model === 'haiku') return

        if (llmData.type === 'llm_request') {
          this.onDebugContext({
            phase,
            type: 'prompt',
            title: `➡️ [${info.name}] ENVOYÉ AU LLM (${llmData.provider}/${llmData.model})`,
            data: {
              systemPrompt: llmData.systemPrompt,
              userMessage: llmData.userMessage,
            },
            keys: ['systemPrompt', 'userMessage'],
          })
        } else if (llmData.type === 'llm_response') {
          this.onDebugContext({
            phase,
            type: 'response',
            title: `⬅️ [${info.name}] RÉPONSE DU LLM (${llmData.usage?.inputTokens}→${llmData.usage?.outputTokens} tokens)`,
            data: {
              content: llmData.content,
            },
            keys: ['content'],
          })
        }
      })
    }

    // Import and run the appropriate agent
    const agent = await this.loadAgent(phase)
    const { provider: phaseProvider, model: phaseModel } = this.getConfigForPhase(phase)

    // Build agent options
    const agentOptions = {
      provider: phaseProvider,
      model: phaseModel,
      phase, // Pass phase for token tracking
      onProgress: (msg) => this.onProgress({ phase, ...msg }),
      // Pass onFileGenerated for real-time file emission (Content agent)
      onFileGenerated: (name, content) => {
        this.generatedFiles[name] = content
        this.onFileGenerated(name, content)
      },
    }

    // SERP agent gets searchProvider option
    if (phase === PHASES.SERP) {
      agentOptions.searchProvider = this.searchProvider
    }

    const result = await agent.run(this.context, agentOptions)

    // Clear LLM listener after agent runs
    if (deepDebug) {
      clearLLMListener()
    }

    // Deduct credits
    deductCredits('agentPhase', info.creditCost)

    // Update context with results
    this.context = {
      ...this.context,
      [phase]: result,
    }

    // Handle generated files
    if (result.files) {
      this.generatedFiles = { ...this.generatedFiles, ...result.files }
      Object.entries(result.files).forEach(([name, content]) => {
        this.onFileGenerated(name, content)
      })
    }

    this.onProgress({ phase, status: 'complete', result })

    // Generate AI summary (non-blocking, for nudge marketing UX)
    this.generateSummary(phase, result)

    return result
  }

  /**
   * Generate AI summary for completed phase (nudge marketing)
   * Uses haiku model for speed and runs in background
   */
  async generateSummary(phase, result) {
    console.log(`📊 [ORCHESTRATOR] Generating summary for ${phase}...`)
    try {
      const summary = await summarize(phase, result, {
        provider: this.provider,
        onProgress: () => {}, // Silent, background operation
      })

      // Get token stats for this phase
      const stats = getStats()
      const phaseStats = stats.byPhase[phase]

      // Merge token stats into summary
      const enrichedSummary = {
        ...summary,
        tokens: phaseStats ? {
          input: phaseStats.inputTokens,
          output: phaseStats.outputTokens,
          cost: phaseStats.cost,
          model: phaseStats.model,
        } : null,
      }

      console.log(`📊 [ORCHESTRATOR] Summary received for ${phase}:`, summary?.headline, phaseStats ? `(${phaseStats.inputTokens}→${phaseStats.outputTokens} tokens)` : '')
      // Callback to update UI with summary
      this.onSummary(phase, enrichedSummary)
      console.log(`📊 [ORCHESTRATOR] onSummary callback called for ${phase}`)
    } catch (error) {
      // Silent fail - summaries are UX enhancement, not critical
      console.error(`📊 [ORCHESTRATOR] Summary generation failed for ${phase}:`, error.message)
    }
  }

  /**
   * Load agent module dynamically
   */
  async loadAgent(phase) {
    const agents = {
      [PHASES.SERP]: () => import('./serp'),
      [PHASES.STRATEGE]: () => import('./stratege'),
      [PHASES.IDENTITE]: () => import('./identite'),
      [PHASES.ARCHITECTE]: () => import('./architecte'),
      [PHASES.REDACTEUR]: () => import('./redacteur'),
      [PHASES.DA]: () => import('./da'),
      [PHASES.CONTENT]: () => import('./content'),
      [PHASES.IMAGES]: () => import('./images'),
    }

    const loader = agents[phase]
    if (!loader) {
      throw new Error(`Unknown phase: ${phase}`)
    }

    const module = await loader()
    return module.default
  }

  /**
   * Run in "Full Auto" mode - domain to site
   */
  async fullAuto(domain) {
    return this.start(domain)
  }

  /**
   * Run PREPARATION phases only (5 agents)
   * Returns context ready for user validation
   */
  async runPreparation(domain) {
    this.domain = domain
    this.phase = PHASES.INIT
    this.context = {
      domain,
      language: this.language,
      cmsPattern: this.cmsPattern,
      targetSite: this.targetSite, // Money site to backlink
      resourceLinks: this.resourceLinks, // Additional links for webography
      antiFootprint: this.antiFootprint,
      maxPages: this.maxPages, // Site size limit
    }

    // Reset token stats for this session
    resetStats()

    this.onPhaseChange(PHASES.INIT)
    console.log('🚀 [ORCHESTRATOR] Starting preparation for:', domain)

    try {
      // Run 6 preparation phases (including SERP)
      console.log('🔄 [ORCHESTRATOR] Running SERP...')
      await this.runPhase(PHASES.SERP)
      console.log('✅ [ORCHESTRATOR] SERP complete')

      console.log('🔄 [ORCHESTRATOR] Running STRATEGE...')
      await this.runPhase(PHASES.STRATEGE)
      console.log('✅ [ORCHESTRATOR] STRATEGE complete')

      console.log('🔄 [ORCHESTRATOR] Running IDENTITE...')
      await this.runPhase(PHASES.IDENTITE)
      console.log('✅ [ORCHESTRATOR] IDENTITE complete')

      console.log('🔄 [ORCHESTRATOR] Running ARCHITECTE...')
      await this.runPhase(PHASES.ARCHITECTE)
      console.log('✅ [ORCHESTRATOR] ARCHITECTE complete, pages:', this.context.architecte?.architecture?.pages?.length || 0)

      console.log('🔄 [ORCHESTRATOR] Running REDACTEUR...')
      await this.runPhase(PHASES.REDACTEUR)
      console.log('✅ [ORCHESTRATOR] REDACTEUR complete')

      console.log('🔄 [ORCHESTRATOR] Running DA...')
      await this.runPhase(PHASES.DA)
      console.log('✅ [ORCHESTRATOR] DA complete')

      console.log('🎉 [ORCHESTRATOR] All preparation phases complete!')
      console.log('📦 [ORCHESTRATOR] Final context keys:', Object.keys(this.context))

      // Get preparation stats
      const stats = getStatsSummary()
      console.log('💰 [ORCHESTRATOR] Prep stats: ' + stats.totalCost + ' (' + stats.calls + ' calls)')
      this.onStats(stats)

      return {
        success: true,
        context: this.context,
        phase: 'preparation_complete',
        stats,
      }
    } catch (error) {
      console.error('❌ [ORCHESTRATOR] Preparation failed at phase:', this.phase, error)
      this.onError(error)
      const stats = getStatsSummary()
      return {
        success: false,
        error: error.message,
        phase: this.phase,
        stats,
      }
    }
  }

  /**
   * Run GENERATION phases (Content + Images)
   * Must be called after runPreparation
   */
  async runGeneration() {
    if (!this.context.da) {
      throw new Error('La phase de préparation doit être terminée avant la génération')
    }

    try {
      await this.runPhase(PHASES.CONTENT)
      await this.runPhase(PHASES.IMAGES)

      this.phase = PHASES.DONE
      this.onPhaseChange(PHASES.DONE)

      // Get final stats (includes prep + generation)
      const stats = getStatsSummary()
      console.log('💰 [ORCHESTRATOR] Generation complete: ' + stats.totalCost + ' total')
      this.onStats(stats)

      // Get images from context (agent images stores results there)
      const generatedImages = this.context.images?.images || []

      // Get link placements from content phase
      const linkPlacements = this.context.content?.linkPlacements || null

      return {
        success: true,
        files: this.generatedFiles,
        images: generatedImages,
        linkPlacements, // Where money site and resource links were placed
        context: this.context,
        stats,
      }
    } catch (error) {
      this.onError(error)
      const stats = getStatsSummary()
      return {
        success: false,
        error: error.message,
        phase: this.phase,
        stats,
      }
    }
  }

  /**
   * Run in "Guided" mode - pause after each phase
   */
  async* guided(domain) {
    this.domain = domain
    this.context = {
      domain,
      language: this.language,
      cmsPattern: this.cmsPattern,
      targetSite: this.targetSite, // Money site to backlink
      resourceLinks: this.resourceLinks, // Additional links for webography
      antiFootprint: this.antiFootprint,
      maxPages: this.maxPages, // Site size limit
    }

    for (const phase of Object.values(PHASES)) {
      if (phase === PHASES.INIT || phase === PHASES.DONE) continue

      const result = await this.runPhase(phase)
      yield { phase, result, context: this.context }
    }

    return { files: this.generatedFiles, context: this.context }
  }

  /**
   * Resume from a specific phase
   */
  async resumeFrom(phase, savedContext) {
    this.context = savedContext
    this.phase = phase

    const phases = Object.values(PHASES)
    const startIndex = phases.indexOf(phase)

    for (let i = startIndex; i < phases.length; i++) {
      const currentPhase = phases[i]
      if (currentPhase === PHASES.INIT || currentPhase === PHASES.DONE) continue
      await this.runPhase(currentPhase)
    }

    return { files: this.generatedFiles, context: this.context }
  }

  /**
   * Export context for saving/resuming
   */
  exportContext() {
    return {
      domain: this.domain,
      phase: this.phase,
      context: this.context,
      files: this.generatedFiles,
      timestamp: new Date().toISOString(),
    }
  }

  /**
   * Import saved context
   */
  importContext(saved) {
    this.domain = saved.domain
    this.phase = saved.phase
    this.context = saved.context
    this.generatedFiles = saved.files || {}
  }
}

/**
 * Create orchestrator instance
 */
export function createOrchestrator(options = {}) {
  return new Orchestrator(options)
}

/**
 * Quick function to run full pipeline
 */
export async function runPipeline(domain, options = {}) {
  const orchestrator = createOrchestrator(options)
  return orchestrator.fullAuto(domain)
}

export default {
  Orchestrator,
  createOrchestrator,
  runPipeline,
  PHASES,
  PHASE_INFO,
  PHASE_TYPES,
  PHASE_TYPE_MAP,
}
